/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_TIMEOUT } from "utils";

applyPlugin(jsPDF);

function PerorangDetail() {
  const [laporanHarian, setLaporanHarian] = useState([]);
  const [dataDaftarHadir, setDataDaftarHadir] = useState([]);
  const [dataHariLibur, setDataHariLibur] = useState([]);
  const [dateRange, setDateRange] = useState({ tanggal_awal: "", tanggal_akhir: "" });
  const [loading, setLoading] = useState(false);
  const [openKegiatanModal, setOpenKegiatanModal] = useState(false);
  const [selectedKegiatan, setSelectedKegiatan] = useState([]);
  const [selectedLaporanTitle, setSelectedLaporanTitle] = useState("");
  const [loadingKegiatan, setLoadingKegiatan] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({
    type: "",
    title: "",
    data: [],
    tanggal: "",
  });
  const [filterDateRange, setFilterDateRange] = useState({
    tanggal_awal: "",
    tanggal_akhir: "",
  });
  const [kegiatanByLaporan, setKegiatanByLaporan] = useState({});
  const navigate = useNavigate();
  const { uid_RSS } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const nama = searchParams.get("nama") || "";

  // Inisialisasi filter dari URL
  useEffect(() => {
    const tAwal = searchParams.get("tanggal_awal");
    const tAkhir = searchParams.get("tanggal_akhir");
    if (tAwal && tAkhir) {
      setFilterDateRange({ tanggal_awal: tAwal, tanggal_akhir: tAkhir });
    }
  }, [searchParams]);

  // Fetch daftar hadir (endpoint sama seperti index.js)
  const fetchDaftarHadir = async (tanggal_awal, tanggal_akhir) => {
    if (!uid_RSS || !tanggal_awal || !tanggal_akhir) return;
    try {
      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/getdh/mingguan",
        {
          tAwal: tanggal_awal,
          tAkhir: tanggal_akhir,
          idUser: uid_RSS,
        }
      );
      if (response.data && response.data.success === 1) {
        setDataDaftarHadir(response.data.data || []);
      } else {
        setDataDaftarHadir([]);
      }
    } catch (err) {
      console.error("Error fetching daftar hadir:", err);
      setDataDaftarHadir([]);
    }
  };

  const fetchHariLibur = async (tanggal_awal, tanggal_akhir) => {
    if (!tanggal_awal || !tanggal_akhir) return;
    try {
      const response = await axios.get(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/libur",
        {
          params: { tanggal_awal, tanggal_akhir },
          timeout: API_TIMEOUT,
        }
      );
      if (response.data?.success === 1) {
        setDataHariLibur(response.data.data || []);
      } else {
        setDataHariLibur([]);
      }
    } catch (err) {
      console.error("Error fetching hari libur:", err);
      setDataHariLibur([]);
    }
  };

  // Fetch laporan harian pegawai (endpoint sama seperti index.js)
  const fetchLaporanHarian = async (customDateRange = null) => {
    if (!uid_RSS) return;
    const tanggal_awal = customDateRange?.tanggal_awal ?? filterDateRange.tanggal_awal;
    const tanggal_akhir = customDateRange?.tanggal_akhir ?? filterDateRange.tanggal_akhir;
    if (!tanggal_awal || !tanggal_akhir) return;

    setDateRange({ tanggal_awal, tanggal_akhir });
    setLoading(true);
    try {
      const [laporanResponse] = await Promise.all([
        axios.get(`${API_ESA_OFFICE}laporan-harian/pegawai/${uid_RSS}`, {
          params: { tanggal_awal, tanggal_akhir },
        }),
        fetchDaftarHadir(tanggal_awal, tanggal_akhir),
        fetchHariLibur(tanggal_awal, tanggal_akhir),
      ]);
      setLaporanHarian(laporanResponse.data || []);
    } catch (err) {
      console.error("Error fetching laporan harian:", err);
      await Promise.all([
        fetchDaftarHadir(tanggal_awal, tanggal_akhir),
        fetchHariLibur(tanggal_awal, tanggal_akhir),
      ]);
      swal("Error", "Gagal mengambil data laporan harian", "error");
      setLaporanHarian([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    const newDateRange = { ...filterDateRange, [field]: value };
    setFilterDateRange(newDateRange);
    if (newDateRange.tanggal_awal && newDateRange.tanggal_akhir) {
      if (newDateRange.tanggal_awal > newDateRange.tanggal_akhir) {
        swal("Error", "Tanggal awal tidak boleh lebih besar dari tanggal akhir", "error");
        return;
      }
      fetchLaporanHarian(newDateRange);
    }
  };

  const handleResetFilter = () => {
    const tAwal = searchParams.get("tanggal_awal");
    const tAkhir = searchParams.get("tanggal_akhir");
    if (tAwal && tAkhir) {
      setFilterDateRange({ tanggal_awal: tAwal, tanggal_akhir: tAkhir });
      fetchLaporanHarian({ tanggal_awal: tAwal, tanggal_akhir: tAkhir });
    }
  };

  useEffect(() => {
    if (uid_RSS && filterDateRange.tanggal_awal && filterDateRange.tanggal_akhir) {
      fetchLaporanHarian(filterDateRange);
    }
  }, [uid_RSS, filterDateRange.tanggal_awal, filterDateRange.tanggal_akhir]);

  const handleOpenDetailModal = (type, data, tanggal) => {
    let title = "";
    switch (type) {
      case "laporan_harian":
        title = `Detail Laporan Harian - ${new Date(tanggal).toLocaleDateString("id-ID")}`;
        break;
      case "instruksi":
        title = `Detail Instruksi Selesai - ${new Date(tanggal).toLocaleDateString("id-ID")}`;
        break;
      case "disposisi":
        title = `Detail Disposisi Selesai - ${new Date(tanggal).toLocaleDateString("id-ID")}`;
        break;
      case "events":
        title = `Detail Event - ${new Date(tanggal).toLocaleDateString("id-ID")}`;
        break;
      case "tamu":
        title = `Detail Tamu - ${new Date(tanggal).toLocaleDateString("id-ID")}`;
        break;
      default:
        title = "Detail";
    }
    setDetailModalData({ type, title, data: data || [], tanggal });
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setDetailModalData({ type: "", title: "", data: [], tanggal: "" });
  };

  const handleOpenKegiatanModal = (id_laporan, tanggal) => {
    setOpenKegiatanModal(true);
    setLoadingKegiatan(true);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle(
      `Kegiatan Laporan Harian - ${new Date(tanggal).toLocaleDateString("id-ID")}`
    );
    axios
      .get(`${API_ESA_OFFICE}laporan-harian/listkegiatan/${id_laporan}`)
      .then((res) => {
        setSelectedKegiatan(res.data.kegiatan || []);
        setLoadingKegiatan(false);
      })
      .catch((err) => {
        console.error("Error fetching kegiatan:", err);
        swal("Error", "Gagal mengambil data kegiatan", "error");
        setSelectedKegiatan([]);
        setLoadingKegiatan(false);
      });
  };

  const handleCloseKegiatanModal = () => {
    setOpenKegiatanModal(false);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle("");
    setLoadingKegiatan(false);
  };

  const generateAllDates = (tanggal_awal, tanggal_akhir) => {
    if (!tanggal_awal || !tanggal_akhir) return [];
    const dates = [];
    const startDate = new Date(tanggal_awal);
    const endDate = new Date(tanggal_akhir);
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    }
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return null;
  };

  const filteredLaporanHarian = useMemo(() => {
    if (!dateRange.tanggal_awal || !dateRange.tanggal_akhir) {
      return laporanHarian;
    }
    const allDates = generateAllDates(dateRange.tanggal_awal, dateRange.tanggal_akhir);
    const laporanMap = new Map();
    laporanHarian.forEach((item) => {
      const tanggalNormalized = normalizeDate(item.tanggal);
      if (tanggalNormalized) laporanMap.set(tanggalNormalized, item);
    });
    const mergedData = allDates.map((date) => {
      const tanggalNormalized = normalizeDate(date.toISOString().split("T")[0]);
      const laporanData = laporanMap.get(tanggalNormalized);
      if (laporanData) return laporanData;
      return {
        tanggal: tanggalNormalized,
        laporan_harian: [],
        instruksi: [],
        disposisi: [],
        events: [],
        tamu: [],
        apel_pagi: null,
        apel_siang: null,
      };
    });
    return mergedData.sort((a, b) => new Date(b.tanggal) - new Date(a.tanggal));
  }, [laporanHarian, dateRange]);

  // Fetch kegiatan untuk semua laporan agar bisa tampilkan detail di kolom
  useEffect(() => {
    const ids = [];
    filteredLaporanHarian.forEach((item) => {
      item.laporan_harian?.forEach((l) => {
        if (l.id_laporan) ids.push(l.id_laporan);
      });
    });
    if (ids.length === 0) {
      setKegiatanByLaporan({});
      return;
    }
    const fetchAll = async () => {
      const results = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await axios.get(`${API_ESA_OFFICE}laporan-harian/listkegiatan/${id}`);
            results[id] = res.data.kegiatan || [];
          } catch {
            results[id] = [];
          }
        })
      );
      setKegiatanByLaporan(results);
    };
    fetchAll();
  }, [filteredLaporanHarian]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const formatTanggal = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const colWidths = {
    no: 50,
    tanggal: 0,
    masukApel: 140,
    laporanHarian: 200,
    instruksi: 140,
    disposisi: 140,
    events: 120,
    tamu: 120,
    pulangApel: 140,
  };

  const columns = [
    { Header: "No", accessor: "index", width: colWidths.no },
    { Header: "Tanggal", accessor: "tanggal", width: colWidths.tanggal || "auto" },
    { Header: "Waktu Masuk & Apel Pagi", accessor: "masuk_apel_pagi", width: colWidths.masukApel },
    { Header: "Laporan Harian & Aksi", accessor: "laporan_harian", width: colWidths.laporanHarian },
    { Header: "Instruksi Selesai", accessor: "instruksi", width: colWidths.instruksi },
    { Header: "Disposisi Selesai", accessor: "disposisi", width: colWidths.disposisi },
    { Header: "Event", accessor: "events", width: colWidths.events },
    { Header: "Tamu", accessor: "tamu", width: colWidths.tamu },
    { Header: "Waktu Pulang & Apel Siang", accessor: "pulang_apel_siang", width: colWidths.pulangApel },
  ];

  const fixedCellSx = (w) => (w === 0 ? {
    minWidth: 150,
    wordBreak: "break-word",
    whiteSpace: "normal",
    maxHeight: 160,
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
  } : {
    width: w,
    maxWidth: w,
    minWidth: w,
    wordBreak: "break-word",
    whiteSpace: "normal",
    maxHeight: 160,
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
  });

  const rows = filteredLaporanHarian.map((item, index) => {
    const firstLaporan = item.laporan_harian && item.laporan_harian.length > 0 ? item.laporan_harian[0] : null;
    const tanggalNormalized = normalizeDate(item.tanggal);
    const daftarHadirData = dataDaftarHadir.find((dh) => normalizeDate(dh.tanggal) === tanggalNormalized);
    const hariLiburData = dataHariLibur.find((hl) => normalizeDate(hl.tanggal) === tanggalNormalized);
    const tanggalObj = new Date(item.tanggal);
    const dayOfWeek = tanggalObj.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const tanggalFormatted = new Date(item.tanggal).toLocaleDateString("id-ID", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return {
      index: index + 1,
      tanggal: (
        <MDBox sx={fixedCellSx(colWidths.tanggal)}>
          <MDTypography variant="caption" color="text" display="block">
            {tanggalFormatted}
          </MDTypography>
          {hariLiburData && (
            <Chip label={`Libur: ${hariLiburData.libur}`} color="warning" size="small" sx={{ mt: 0.5, wordBreak: "break-word", whiteSpace: "normal" }} />
          )}
          {!hariLiburData && isWeekend && (
            <Chip label="Bukan Hari Kerja" color="secondary" size="small" sx={{ mt: 0.5 }} />
          )}
        </MDBox>
      ),
      masuk_apel_pagi: (
        <MDBox sx={fixedCellSx(colWidths.masukApel)}>
          {daftarHadirData?.waktuMasuk && (
            <MDBox mb={0.5}>
              <MDTypography variant="caption" color="text" display="block">
                {daftarHadirData.status === "Hadir" || daftarHadirData.status === "Dinas Luar" ? "Masuk" : "Lapor"}: {daftarHadirData.waktuMasuk}
              </MDTypography>
              {daftarHadirData.status && (
                <Chip label={daftarHadirData.status} color={daftarHadirData.status === "Hadir" ? "success" : daftarHadirData.status === "Izin" ? "warning" : "error"} size="small" variant="filled" sx={{ mt: 0.5 }} />
              )}
            </MDBox>
          )}
          {item.apel_pagi ? (
            <MDTypography variant="caption" color="text" display="block" mt={0.5}>Apel: {item.apel_pagi.jam_absen || "-"}</MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary" display="block" mt={0.5}>Belum Apel Pagi</MDTypography>
          )}
        </MDBox>
      ),
      laporan_harian: (
        <MDBox sx={fixedCellSx(colWidths.laporanHarian)}>
          {item.laporan_harian && item.laporan_harian.length > 0 ? (
            <MDBox>
              <Chip label={`${item.laporan_harian.length} Laporan`} color="info" size="small" sx={{ mb: 0.5, cursor: "pointer" }} onClick={() => handleOpenDetailModal("laporan_harian", item.laporan_harian, item.tanggal)} />
              {item.laporan_harian.map((l, i) => {
                const kegiatanList = kegiatanByLaporan[l.id_laporan] || [];
                return (
                  <MDBox key={i} sx={{ mb: 1, pb: 1, borderBottom: i < item.laporan_harian.length - 1 ? "1px solid" : "none", borderColor: "divider" }}>
                    <MDTypography variant="caption" fontWeight="bold" color="info" display="block" sx={{ py: 0.25 }}>
                      Laporan #{i + 1} ({l.jumlah_kegiatan || 0} Kegiatan)
                    </MDTypography>
                    {kegiatanList.length > 0 ? (
                      kegiatanList.map((k, ki) => (
                        <MDBox key={ki} sx={{ pl: 1, mt: 0.5 }}>
                          <MDTypography variant="caption" color="text" display="block" sx={{ py: 0.25 }}>
                            • Kegiatan: {k.kegiatan || "-"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary" display="block" sx={{ pl: 1, py: 0.25 }}>
                            Output: {k.output || "-"}
                            {(k.jumlah_output != null || k.satuan_output) ? ` (${k.jumlah_output ?? "-"} ${k.satuan_output ?? ""})`.trim() : ""}
                          </MDTypography>
                        </MDBox>
                      ))
                    ) : (
                      <MDTypography variant="caption" color="text.secondary" display="block" sx={{ pl: 1 }}>Memuat...</MDTypography>
                    )}
                  </MDBox>
                );
              })}
              <MDButton variant="outlined" color="info" size="small" sx={{ mt: 0.5 }} onClick={() => handleOpenKegiatanModal(firstLaporan.id_laporan, item.tanggal)}>Lihat Kegiatan</MDButton>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          )}
        </MDBox>
      ),
      instruksi: (
        <MDBox sx={fixedCellSx(colWidths.instruksi)}>
          {(() => {
            const instruksiTerima = item.instruksi?.filter((i) => i.jenis === "terima") || [];
            const instruksiBeri = item.instruksi?.filter((i) => i.jenis === "beri") || [];
            const total = item.instruksi?.length || 0;
            if (total === 0) return <MDTypography variant="caption" color="text.secondary">-</MDTypography>;
            return (
              <MDBox>
                {instruksiTerima.length > 0 && (
                  <MDBox mb={0.5}>
                    <Chip label={`Terima: ${instruksiTerima.length}`} color="success" size="small" sx={{ mb: 0.5, cursor: "pointer" }} onClick={() => handleOpenDetailModal("instruksi", instruksiTerima, item.tanggal)} />
                    {instruksiTerima.map((inst, i) => (
                      <MDTypography key={i} variant="caption" color="text" display="block" sx={{ py: 0.25 }}>• {inst.perihal || "-"}</MDTypography>
                    ))}
                  </MDBox>
                )}
                {instruksiBeri.length > 0 && (
                  <MDBox>
                    <Chip label={`Beri: ${instruksiBeri.length}`} color="info" size="small" sx={{ mb: 0.5, cursor: "pointer" }} onClick={() => handleOpenDetailModal("instruksi", instruksiBeri, item.tanggal)} />
                    {instruksiBeri.map((inst, i) => (
                      <MDTypography key={i} variant="caption" color="text" display="block" sx={{ py: 0.25 }}>• {inst.perihal || "-"}</MDTypography>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            );
          })()}
        </MDBox>
      ),
      disposisi: (
        <MDBox sx={fixedCellSx(colWidths.disposisi)}>
          {(() => {
            const disposisiTerima = item.disposisi?.filter((d) => d.jenis === "terima") || [];
            const disposisiBeri = item.disposisi?.filter((d) => d.jenis === "beri") || [];
            const total = item.disposisi?.length || 0;
            if (total === 0) return <MDTypography variant="caption" color="text.secondary">-</MDTypography>;
            return (
              <MDBox>
                {disposisiTerima.length > 0 && (
                  <MDBox mb={0.5}>
                    <Chip label={`Terima: ${disposisiTerima.length}`} color="warning" size="small" sx={{ mb: 0.5, cursor: "pointer" }} onClick={() => handleOpenDetailModal("disposisi", disposisiTerima, item.tanggal)} />
                    {disposisiTerima.map((disp, i) => (
                      <MDTypography key={i} variant="caption" color="text" display="block" sx={{ py: 0.25 }}>• {disp.perihal || disp.nomor_surat || "-"}</MDTypography>
                    ))}
                  </MDBox>
                )}
                {disposisiBeri.length > 0 && (
                  <MDBox>
                    <Chip label={`Beri: ${disposisiBeri.length}`} color="info" size="small" sx={{ mb: 0.5, cursor: "pointer" }} onClick={() => handleOpenDetailModal("disposisi", disposisiBeri, item.tanggal)} />
                    {disposisiBeri.map((disp, i) => (
                      <MDTypography key={i} variant="caption" color="text" display="block" sx={{ py: 0.25 }}>• {disp.perihal || disp.nomor_surat || "-"}</MDTypography>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            );
          })()}
        </MDBox>
      ),
      events: (
        <MDBox sx={fixedCellSx(colWidths.events)}>
          {item.events && item.events.length > 0 ? (
            <MDBox>
              <Chip label={`${item.events.length} Event`} color="primary" size="small" sx={{ mb: 0.5, cursor: "pointer" }} onClick={() => handleOpenDetailModal("events", item.events, item.tanggal)} />
              {item.events.map((ev, i) => (
                <MDTypography key={i} variant="caption" color="text" display="block" sx={{ py: 0.25 }}>• {ev.nama_event || "-"}</MDTypography>
              ))}
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          )}
        </MDBox>
      ),
      tamu: (
        <MDBox sx={fixedCellSx(colWidths.tamu)}>
          {item.tamu && item.tamu.length > 0 ? (
            <MDBox>
              <Chip label={`${item.tamu.length} Tamu`} color="secondary" size="small" sx={{ mb: 0.5, cursor: "pointer" }} onClick={() => handleOpenDetailModal("tamu", item.tamu, item.tanggal)} />
              {item.tamu.map((t, i) => (
                <MDTypography key={i} variant="caption" color="text" display="block" sx={{ py: 0.25 }}>• {t.nama_tamu || "-"}</MDTypography>
              ))}
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          )}
        </MDBox>
      ),
      pulang_apel_siang: (
        <MDBox sx={fixedCellSx(colWidths.pulangApel)}>
          {daftarHadirData?.waktuPulang && (
            <MDBox mb={0.5}>
              <MDTypography variant="caption" color="text" display="block">Pulang: {daftarHadirData.waktuPulang}</MDTypography>
              {daftarHadirData.workingHours && (
                <MDTypography variant="caption" color="info" display="block" sx={{ fontWeight: "bold", mt: 0.5 }}>Jam Kerja: {daftarHadirData.workingHours}</MDTypography>
              )}
            </MDBox>
          )}
          {item.apel_siang ? (
            <MDTypography variant="caption" color="text" display="block" mt={0.5}>Apel: {item.apel_siang.jam_absen || "-"}</MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary" display="block" mt={0.5}>Belum Apel Siang</MDTypography>
          )}
        </MDBox>
      ),
    };
  });

  const totalLaporan = filteredLaporanHarian.reduce((sum, item) => sum + (item.laporan_harian?.length || 0), 0);
  const totalKegiatan = filteredLaporanHarian.reduce((sum, item) => sum + (item.laporan_harian?.reduce((s, l) => s + (l.jumlah_kegiatan || 0), 0) || 0), 0);

  const parseJamKerjaToMenit = (str) => {
    if (!str || typeof str !== "string") return 0;
    const m1 = str.match(/(\d+)\s*jam\s*(\d+)\s*menit/);
    if (m1) return parseInt(m1[1], 10) * 60 + parseInt(m1[2], 10);
    const m2 = str.match(/(\d+)\s*jam/);
    if (m2) return parseInt(m2[1], 10) * 60;
    const m3 = str.match(/^(\d+):(\d+)$/);
    if (m3) return parseInt(m3[1], 10) * 60 + parseInt(m3[2], 10);
    return 0;
  };

  const formatMenitToJam = (menit) => {
    if (!menit || menit < 0) return "0 jam 0 menit";
    const jam = Math.floor(menit / 60);
    const m = menit % 60;
    return `${jam} jam ${m} menit`;
  };

  const rekapan = useMemo(() => {
    let totalJamKerjaMenit = 0;
    let totalHariKerja = 0;
    let totalEvent = 0;
    let totalDisposisi = 0;
    let totalTugas = 0;
    filteredLaporanHarian.forEach((item) => {
      const tanggalNormalized = normalizeDate(item.tanggal);
      const dh = dataDaftarHadir.find((d) => normalizeDate(d.tanggal) === tanggalNormalized);
      if (dh?.workingHours) totalJamKerjaMenit += parseJamKerjaToMenit(dh.workingHours);
      if (dh && (dh.status === "Hadir" || dh.status === "Dinas Luar")) totalHariKerja += 1;
      totalEvent += item.events?.length || 0;
      totalDisposisi += item.disposisi?.length || 0;
      totalTugas += item.instruksi?.length || 0;
    });
    return {
      totalJamKerja: formatMenitToJam(totalJamKerjaMenit),
      totalHariKerja,
      totalLaporan,
      totalEvent,
      totalDisposisi,
      totalTugas,
    };
  }, [filteredLaporanHarian, dataDaftarHadir, totalLaporan]);

  // Data plain untuk export Excel & PDF (selaras dengan kolom tabel, termasuk detail)
  const exportData = useMemo(() => {
    return filteredLaporanHarian.map((item, index) => {
      const tanggalNormalized = normalizeDate(item.tanggal);
      const dh = dataDaftarHadir.find((d) => normalizeDate(d.tanggal) === tanggalNormalized);
      const hl = dataHariLibur.find((d) => normalizeDate(d.tanggal) === tanggalNormalized);
      const tanggalObj = new Date(item.tanggal);
      const isWeekend = tanggalObj.getDay() === 0 || tanggalObj.getDay() === 6;
      const hari = formatDate(item.tanggal).split(",")[0] || "";
      const hariLabel = hl ? `${hari} (Libur: ${hl.libur})` : isWeekend ? `${hari} (Bukan Hari Kerja)` : hari;

      let waktuMasukApelPagi = "";
      if (dh?.waktuMasuk) {
        waktuMasukApelPagi += (dh.status === "Hadir" || dh.status === "Dinas Luar" ? "Masuk" : "Lapor") + ": " + dh.waktuMasuk;
        if (dh.status) waktuMasukApelPagi += " (" + dh.status + ")";
      }
      if (item.apel_pagi) {
        waktuMasukApelPagi += (waktuMasukApelPagi ? " | " : "") + "Apel: " + (item.apel_pagi.jam_absen || "-");
      } else {
        waktuMasukApelPagi += (waktuMasukApelPagi ? " | " : "") + "Belum Apel Pagi";
      }
      if (!waktuMasukApelPagi) waktuMasukApelPagi = "-";

      let laporanHarianAksi = "-";
      if (item.laporan_harian?.length) {
        const parts = [];
        item.laporan_harian.forEach((l, i) => {
          const kegiatanList = kegiatanByLaporan[l.id_laporan] || [];
          if (kegiatanList.length > 0) {
            kegiatanList.forEach((k) => {
              parts.push(`Kegiatan: ${k.kegiatan || "-"} | Output: ${k.output || "-"} ${(k.jumlah_output != null || k.satuan_output) ? `(${k.jumlah_output ?? "-"} ${k.satuan_output ?? ""})` : ""}`);
            });
          } else {
            parts.push(`Laporan #${i + 1}: ${l.jumlah_kegiatan || 0} Kegiatan`);
          }
        });
        laporanHarianAksi = parts.length ? parts.join("; ") : item.laporan_harian.map((l, i) => `Laporan #${i + 1}: ${l.jumlah_kegiatan || 0} Kegiatan`).join("; ");
      }

      let instruksiSelesai = "-";
      if (item.instruksi?.length) {
        const terima = item.instruksi.filter((i) => i.jenis === "terima");
        const beri = item.instruksi.filter((i) => i.jenis === "beri");
        const parts = [];
        if (terima.length) parts.push("Terima: " + terima.map((t) => t.perihal || "-").join(" | "));
        if (beri.length) parts.push("Beri: " + beri.map((b) => b.perihal || "-").join(" | "));
        instruksiSelesai = parts.join("; ");
      }

      let disposisiSelesai = "-";
      if (item.disposisi?.length) {
        const terima = item.disposisi.filter((d) => d.jenis === "terima");
        const beri = item.disposisi.filter((d) => d.jenis === "beri");
        const parts = [];
        if (terima.length) parts.push("Terima: " + terima.map((d) => d.perihal || d.nomor_surat || "-").join(" | "));
        if (beri.length) parts.push("Beri: " + beri.map((d) => d.perihal || d.nomor_surat || "-").join(" | "));
        disposisiSelesai = parts.join("; ");
      }

      let eventsText = "-";
      if (item.events?.length) {
        eventsText = item.events.map((e) => e.nama_event || "-").join(" | ");
      }

      let tamuText = "-";
      if (item.tamu?.length) {
        tamuText = item.tamu.map((t) => t.nama_tamu || "-").join(" | ");
      }

      let waktuPulangApelSiang = "";
      if (dh?.waktuPulang) {
        waktuPulangApelSiang += "Pulang: " + dh.waktuPulang;
        if (dh.workingHours) waktuPulangApelSiang += " | Jam Kerja: " + dh.workingHours;
      }
      if (item.apel_siang) {
        waktuPulangApelSiang += (waktuPulangApelSiang ? " | " : "") + "Apel: " + (item.apel_siang.jam_absen || "-");
      } else {
        waktuPulangApelSiang += (waktuPulangApelSiang ? " | " : "") + "Belum Apel Siang";
      }
      if (!waktuPulangApelSiang) waktuPulangApelSiang = "-";

      return {
        No: index + 1,
        Tanggal: formatTanggal(item.tanggal),
        Hari: hariLabel,
        "Waktu Masuk & Apel Pagi": waktuMasukApelPagi,
        "Laporan Harian & Aksi": laporanHarianAksi,
        "Instruksi Selesai": instruksiSelesai,
        "Disposisi Selesai": disposisiSelesai,
        Event: eventsText,
        Tamu: tamuText,
        "Waktu Pulang & Apel Siang": waktuPulangApelSiang,
      };
    });
  }, [filteredLaporanHarian, dataDaftarHadir, dataHariLibur, kegiatanByLaporan]);

  const getDownloadInfo = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userNama = user?.nama || "User";
    const tanggalDownload = new Date().toLocaleString("id-ID", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return { userNama, tanggalDownload };
  };

  const handleExportExcel = () => {
    if (!exportData.length) {
      swal({ title: "Info", text: "Tidak ada data untuk di-export", icon: "info", button: "OK" });
      return;
    }
    const tAwal = filterDateRange.tanggal_awal;
    const tAkhir = filterDateRange.tanggal_akhir;
    const { userNama, tanggalDownload } = getDownloadInfo();

    const headerRows = [
      ["Detail Laporan Harian - " + (nama || "Pegawai")],
      ["Periode: " + formatTanggal(tAwal) + " s/d " + formatTanggal(tAkhir)],
      [],
      ["Rekapan:"],
      [`Total Jam Kerja: ${rekapan.totalJamKerja} | Total Hari Kerja: ${rekapan.totalHariKerja} | Total Laporan: ${rekapan.totalLaporan}`],
      [`Total Event: ${rekapan.totalEvent} | Total Disposisi: ${rekapan.totalDisposisi} | Total Tugas: ${rekapan.totalTugas}`],
      [],
    ];
    const colKeys = ["No", "Tanggal", "Hari", "Waktu Masuk & Apel Pagi", "Laporan Harian & Aksi", "Instruksi Selesai", "Disposisi Selesai", "Event", "Tamu", "Waktu Pulang & Apel Siang"];
    const dataRows = exportData.map((row) => colKeys.map((k) => row[k] ?? ""));
    const footerRows = [
      [],
      ["Laporan ini didownload dari Esa Office App oleh " + userNama + " tanggal " + tanggalDownload],
    ];

    const allRows = [...headerRows, colKeys, ...dataRows, ...footerRows];
    const worksheet = XLSX.utils.aoa_to_sheet(allRows);
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 16 },
      { wch: 30 },
      { wch: 30 },
      { wch: 60 },
      { wch: 40 },
      { wch: 40 },
      { wch: 35 },
      { wch: 35 },
      { wch: 45 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Detail Laporan Harian");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `detail-laporan-harian-${nama || "pegawai"}-${tAwal}-${tAkhir}.xlsx`);
  };

  const sanitizeForPDF = (val) => {
    if (val == null) return "";
    const s = String(val).replace(/\r\n/g, " ").replace(/\n/g, " ");
    return s.length > 500 ? s.substring(0, 500) + "..." : s;
  };

  const handleExportPDF = () => {
    if (!exportData.length) {
      swal({ title: "Info", text: "Tidak ada data untuk di-export", icon: "info", button: "OK" });
      return;
    }
    const tAwal = filterDateRange.tanggal_awal;
    const tAkhir = filterDateRange.tanggal_akhir;
    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      let yPos = 12;

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Detail Laporan Harian - " + (nama || "Pegawai"), 14, yPos);
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Periode: " + formatTanggal(tAwal) + " s/d " + formatTanggal(tAkhir), 14, yPos);
      yPos += 8;

      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Rekapan:", 14, yPos);
      yPos += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(`Total Jam Kerja: ${rekapan.totalJamKerja}  |  Total Hari Kerja: ${rekapan.totalHariKerja}  |  Total Laporan: ${rekapan.totalLaporan}`, 14, yPos);
      yPos += 5;
      doc.text(`Total Event: ${rekapan.totalEvent}  |  Total Disposisi: ${rekapan.totalDisposisi}  |  Total Tugas: ${rekapan.totalTugas}`, 14, yPos);
      yPos += 8;

      const headers = [["No", "Tanggal", "Hari", "Waktu Masuk & Apel Pagi", "Laporan Harian & Aksi", "Instruksi Selesai", "Disposisi Selesai", "Event", "Tamu", "Waktu Pulang & Apel Siang"]];
      const body = exportData.map((row) => [
        sanitizeForPDF(row.No),
        sanitizeForPDF(row.Tanggal),
        sanitizeForPDF(row.Hari),
        sanitizeForPDF(row["Waktu Masuk & Apel Pagi"]),
        sanitizeForPDF(row["Laporan Harian & Aksi"]),
        sanitizeForPDF(row["Instruksi Selesai"]),
        sanitizeForPDF(row["Disposisi Selesai"]),
        sanitizeForPDF(row.Event),
        sanitizeForPDF(row.Tamu),
        sanitizeForPDF(row["Waktu Pulang & Apel Siang"]),
      ]);
      doc.autoTable({
        head: headers,
        body,
        startY: yPos,
        styles: { fontSize: 6 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 6 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 20 },
          2: { cellWidth: 22 },
          3: { cellWidth: 28 },
          4: { cellWidth: 45 },
          5: { cellWidth: 28 },
          6: { cellWidth: 28 },
          7: { cellWidth: 25 },
          8: { cellWidth: 25 },
          9: { cellWidth: 35 },
        },
      });

      const { userNama, tanggalDownload } = getDownloadInfo();
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text("Laporan ini didownload dari Esa Office App oleh " + userNama + " tanggal " + tanggalDownload, 14, doc.internal.pageSize.getHeight() - 10);

      doc.save(`detail-laporan-harian-${nama || "pegawai"}-${tAwal}-${tAkhir}.pdf`);
    } catch (err) {
      console.error("PDF export error:", err);
      swal("Error", "Gagal export PDF: " + (err.message || "Unknown error"), "error");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <MDTypography variant="h4">
                Detail Laporan Harian - {nama}
              </MDTypography>
            </Grid>
            <Grid item>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={handleExportExcel}
                  disabled={!exportData.length}
                >
                  <Icon>description</Icon>
                  &nbsp;Export Excel
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="error"
                  onClick={handleExportPDF}
                  disabled={!exportData.length}
                >
                  <Icon>picture_as_pdf</Icon>
                  &nbsp;Export PDF
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => navigate(-1)}
                >
                  <Icon>arrow_back</Icon>&nbsp;Kembali
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Statistik */}
        <MDBox mb={3}>
          <MDTypography variant="h6" color="text" mb={2}>
            Statistik Laporan Harian
          </MDTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Chip
                icon={<Icon>description</Icon>}
                label={`Total Hari: ${filteredLaporanHarian.length}`}
                color="primary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>list</Icon>}
                label={`Total Laporan: ${totalLaporan}`}
                color="info"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>trending_up</Icon>}
                label={`Total Kegiatan: ${totalKegiatan}`}
                color="success"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
          </Grid>
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text.secondary">
              Periode: {filterDateRange.tanggal_awal ? formatTanggal(filterDateRange.tanggal_awal) : "..."} s/d{" "}
              {filterDateRange.tanggal_akhir ? formatTanggal(filterDateRange.tanggal_akhir) : "..."}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mb={2}>
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="date" label="Tanggal Awal" value={filterDateRange.tanggal_awal} onChange={(e) => handleDateRangeChange("tanggal_awal", e.target.value)} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="date" label="Tanggal Akhir" value={filterDateRange.tanggal_akhir} onChange={(e) => handleDateRangeChange("tanggal_akhir", e.target.value)} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDButton variant="outlined" color="secondary" onClick={handleResetFilter} fullWidth>Reset Filter</MDButton>
            </Grid>
          </Grid>
          <MDTypography variant="h6" color="text">
            Data Laporan Harian: {filterDateRange.tanggal_awal ? formatTanggal(filterDateRange.tanggal_awal) : "..."} s/d{" "}
            {filterDateRange.tanggal_akhir ? formatTanggal(filterDateRange.tanggal_akhir) : "..."}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Total: {filteredLaporanHarian.length} hari (Diurutkan berdasarkan tanggal)
          </MDTypography>
        </MDBox>

        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={1}>
            {loading && (
              <MDTypography variant="button" color="text.secondary">
                Memuat...
              </MDTypography>
            )}
            {!loading &&
              filteredLaporanHarian.map((row, index) => {
                const hari = formatDate(row.tanggal).split(",")[0];
                const tanggalNormalized = normalizeDate(row.tanggal);
                const hariLiburData = dataHariLibur.find((hl) => normalizeDate(hl.tanggal) === tanggalNormalized);
                const dayOfWeek = new Date(row.tanggal).getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const hariLabel = hariLiburData ? `${hari} (Libur: ${hariLiburData.libur})` : isWeekend ? `${hari} (Bukan Hari Kerja)` : hari;
                return (
                  <Card
                    key={`${row.id_laporan || row.tanggal || "row"}-${index}`}
                  >
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <MDTypography variant="button" fontWeight="bold">
                            {index + 1}. {formatTanggal(row.tanggal)} ({hariLabel})
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDBox
                            mt={0.5}
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Chip
                              label={`Jumlah Kegiatan: ${
                                row.laporan_harian?.reduce((s, l) => s + (l.jumlah_kegiatan || 0), 0) || 0
                              }`}
                              color="info"
                              size="small"
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12}>
                          <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            onClick={() =>
                              handleOpenKegiatanModal(
                                row.laporan_harian?.[0]?.id_laporan,
                                row.tanggal
                              )
                            }
                            sx={{ minWidth: "auto", px: 1 }}
                          >
                            <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                            &nbsp;Lihat Kegiatan
                          </MDButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
          </MDBox>
        ) : (
          <MDBox sx={{ overflowX: "auto", "& table": { tableLayout: "auto", width: "100%", minWidth: 1160 } }}>
            <DataTable
              table={{
                columns,
                rows,
              }}
              entriesPerPage={false}
              showTotalEntries={false}
              isSorted={false}
              noEndBorder
              loading={loading}
            />
          </MDBox>
        )}

        {filteredLaporanHarian.length === 0 && !loading && filterDateRange.tanggal_awal && filterDateRange.tanggal_akhir && (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
          >
            <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
              assessment
            </Icon>
            <MDTypography variant="h6" color="text.secondary">
              Tidak ada data laporan harian
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Coba periksa kembali periode yang dipilih
            </MDTypography>
          </MDBox>
        )}

        {loading && (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <MDTypography variant="h6" color="text.secondary">
              Memuat data...
            </MDTypography>
          </MDBox>
        )}

        {/* Modal Kegiatan */}
        <Dialog
          open={openKegiatanModal}
          onClose={handleCloseKegiatanModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{selectedLaporanTitle}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {loadingKegiatan ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Memuat data kegiatan...
                  </MDTypography>
                </MDBox>
              ) : selectedKegiatan.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada kegiatan untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" color="info" gutterBottom>
                      Total Kegiatan: {selectedKegiatan.length}
                    </MDTypography>
                  </MDBox>
                  <Grid container spacing={2}>
                    {selectedKegiatan.map((kegiatan, index) => (
                      <Grid item xs={12} key={index}>
                        <Card sx={{ p: 2, mb: 2 }}>
                          <MDBox>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <MDTypography
                                variant="h6"
                                color="info"
                                gutterBottom
                              >
                                Kegiatan {index + 1}
                              </MDTypography>
                            </MDBox>

                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Deskripsi Kegiatan:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.kegiatan}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Jumlah Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.jumlah_output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Satuan Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.satuan_output}
                                </MDTypography>
                              </Grid>
                            </Grid>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseKegiatanModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Dialog Detail Modal (sama seperti index.js) */}
        <Dialog open={openDetailModal} onClose={handleCloseDetailModal} maxWidth="md" fullWidth>
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5">{detailModalData.title}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {detailModalData.data.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">Tidak ada data untuk ditampilkan</MDTypography>
                </MDBox>
              ) : (
                <>
                  {detailModalData.type === "laporan_harian" && (
                    <MDBox>
                      {detailModalData.data.map((laporan, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="info" gutterBottom>Laporan #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">Tanggal: {new Date(laporan.tanggal).toLocaleDateString("id-ID")}</MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Jumlah Kegiatan:</MDTypography>
                              <MDTypography variant="body2" color="text">{laporan.jumlah_kegiatan || 0}</MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>ID Laporan:</MDTypography>
                              <MDTypography variant="body2" color="text">{laporan.id_laporan}</MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "instruksi" && (
                    <MDBox>
                      {detailModalData.data.map((instruksi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="success" gutterBottom>Instruksi #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">Selesai: {instruksi.waktu_selesai ? new Date(instruksi.waktu_selesai).toLocaleString("id-ID") : "-"}</MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Perihal:</MDTypography><MDTypography variant="body2" color="text">{instruksi.perihal || "-"}</MDTypography></Grid>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Isi Instruksi:</MDTypography><MDTypography variant="body2" color="text">{instruksi.isi_instruksi || "-"}</MDTypography></Grid>
                            <Grid item xs={12} md={6}><MDTypography variant="subtitle2" color="text" gutterBottom>Prioritas:</MDTypography><Chip label={instruksi.prioritas || "-"} color={instruksi.prioritas === "Tinggi" ? "error" : instruksi.prioritas === "Sedang" ? "warning" : "default"} size="small" /></Grid>
                            <Grid item xs={12} md={6}><MDTypography variant="subtitle2" color="text" gutterBottom>Pemberi Perintah:</MDTypography><MDTypography variant="body2" color="text">{instruksi.pemberi_perintah || "-"}</MDTypography></Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "disposisi" && (
                    <MDBox>
                      {detailModalData.data.map((disposisi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="warning" gutterBottom>Disposisi #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">Selesai: {disposisi.waktu_selesai ? new Date(disposisi.waktu_selesai).toLocaleString("id-ID") : "-"}</MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Perihal:</MDTypography><MDTypography variant="body2" color="text">{disposisi.perihal || "-"}</MDTypography></Grid>
                            <Grid item xs={12} md={6}><MDTypography variant="subtitle2" color="text" gutterBottom>Nomor Surat:</MDTypography><MDTypography variant="body2" color="text">{disposisi.nomor_surat || "-"}</MDTypography></Grid>
                            <Grid item xs={12} md={6}><MDTypography variant="subtitle2" color="text" gutterBottom>Pemberi Perintah:</MDTypography><MDTypography variant="body2" color="text">{disposisi.pemberi_perintah || "-"}</MDTypography></Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "events" && (
                    <MDBox>
                      {detailModalData.data.map((event, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="primary" gutterBottom>Event #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">Waktu Absen: {event.waktu ? new Date(event.waktu).toLocaleString("id-ID") : "-"}</MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Nama Event:</MDTypography><MDTypography variant="body2" color="text">{event.nama_event || "-"}</MDTypography></Grid>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Detail Kegiatan:</MDTypography><MDTypography variant="body2" color="text">{event.detail_kegiatan || "-"}</MDTypography></Grid>
                            <Grid item xs={12} md={6}><MDTypography variant="subtitle2" color="text" gutterBottom>Tempat:</MDTypography><MDTypography variant="body2" color="text">{event.tempat || "-"}</MDTypography></Grid>
                            <Grid item xs={12} md={6}><MDTypography variant="subtitle2" color="text" gutterBottom>Waktu Mulai:</MDTypography><MDTypography variant="body2" color="text">{event.waktu_mulai ? new Date(event.waktu_mulai).toLocaleString("id-ID") : "-"}</MDTypography></Grid>
                            <Grid item xs={12} md={6}><MDTypography variant="subtitle2" color="text" gutterBottom>Waktu Selesai:</MDTypography><MDTypography variant="body2" color="text">{event.waktu_finish ? new Date(event.waktu_finish).toLocaleString("id-ID") : "-"}</MDTypography></Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "tamu" && (
                    <MDBox>
                      {detailModalData.data.map((tamu, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="secondary" gutterBottom>Tamu #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">Waktu Kedatangan: {tamu.waktu_kedatangan ? new Date(tamu.waktu_kedatangan).toLocaleString("id-ID") : "-"}</MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Nama Tamu:</MDTypography><MDTypography variant="body2" color="text">{tamu.nama_tamu || "-"}</MDTypography></Grid>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Instansi:</MDTypography><MDTypography variant="body2" color="text">{tamu.nama_instansi || "-"}</MDTypography></Grid>
                            <Grid item xs={12}><MDTypography variant="subtitle2" color="text" gutterBottom>Keperluan:</MDTypography><MDTypography variant="body2" color="text">{tamu.keperluan || "-"}</MDTypography></Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDetailModal}>Tutup</MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default PerorangDetail;
